import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getProductCategories, getProductCategoriesId } from "./category/category";
import {
  getProductById,
  getRelatedProducts,
  getAllProducts,
  getAllSearchResults,
} from "./product/product";
import { productComment } from "./comments/comment";
import {
  getAllProducts as getAllProductsFromAllProduct,
  topProduct,
  topBanners,
  topAllProduct,
} from "./product/allProduct";
import { getStores } from "./store/stores";
import { getBasketV2 } from "./orders/basketV2";
import { getHomeFeed } from "./home/homeFeed";
import { getStoreProducts } from "./menu/storeProducts";
import type { StoresOrdering } from "../types/types";

// Categories
export const useCategories = () => {
  return useQuery({
    queryKey: ["categories"],
    queryFn: getProductCategories,
    retry: 1,
    retryDelay: 1000,
  });
};

// Product by ID
export const useProductById = (id: number) => {
  return useQuery({
    queryKey: ["product", id],
    queryFn: () => getProductById(id),
    enabled: !!id,
  });
};

// Related Products
export const useRelatedProducts = (id: string) => {
  return useQuery({
    queryKey: ["relatedProducts", id],
    queryFn: () => getRelatedProducts(id),
    enabled: !!id,
  });
};

// All Products
export const useAllProducts = () => {
  return useInfiniteQuery({
    queryKey: ["allProducts"],
    queryFn: ({ pageParam = 1 }) => getAllProducts(pageParam), // pageParam ni uzatamiz
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const url = new URL(lastPage.next);
      const nextPage = url.searchParams.get("page");
      return nextPage ? Number(nextPage) : undefined; // string → number
    },
    initialPageParam: 1,
  });
};

// Product Comments
export const useProductComments = (id: number) => {
  return useQuery({
    queryKey: ["productComments", id],
    queryFn: () => productComment(id),
    enabled: !!id,
  });
};

// Top Products
export const useTopProducts = () => {
  return useQuery({
    queryKey: ["topProducts"],
    queryFn: topProduct,
    retry: false,
  });
};

// Top Banners
export const useTopBanners = () => {
  return useQuery({
    queryKey: ["topBanners"],
    queryFn: topBanners,
    retry: false,
  });
};

// Top All Products
export const useTopAllProducts = () => {
  return useQuery({
    queryKey: ["topAllProducts"],
    queryFn: topAllProduct,
    retry: false,
  });
};

// search product

export const useAllSearchResults = (query: string) => {
  return useInfiniteQuery({
    queryKey: ["searchProducts", query],
    queryFn: getAllSearchResults,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const url = new URL(lastPage.next);
      const nextPage = url.searchParams.get("page");
      return nextPage ? Number(nextPage) : undefined; // ✅ string -> number
    },
    enabled: !!query,
    initialPageParam: 1,
  });
};


// category 
export const useCategoryProducts = (id: number) => {
  return useInfiniteQuery({
    queryKey: ["categoryProducts", id],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getProductCategoriesId(id, pageParam);
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      // Agar `next` mavjud bo‘lsa, undan keyingi sahifa raqamini olish
      if (lastPage.next) {
        const nextPage = new URL(lastPage.next).searchParams.get("page");
        return nextPage ? Number(nextPage) : undefined;
      }
      return undefined;
    },
  });
};

// A small set of top stores (by product count) for the Home page preview section
export const useTopStores = (limit = 8) => {
  return useQuery({
    queryKey: ["stores", "top", limit],
    queryFn: () => getStores({ ordering: "-products_count", limit }),
  });
};

// Home Feed — bosh sahifa uchun banner va do'kon qatorlarini bitta so'rovda qaytaradi
export const useHomeFeed = () => {
  return useQuery({
    queryKey: ["home-feed"],
    queryFn: getHomeFeed,
    retry: false,
  });
};

// Basket v2 — savat do'kon bo'yicha guruhlangan holda (faqat tizimga kirgan foydalanuvchi uchun)
// Savat tez-tez o'zgarib turadi (qo'shish/o'chirish/checkout), shuning uchun global
// 5 daqiqalik staleTime'ga ishonmaydi — sahifa har ochilganda yangidan so'raladi
export const useBasketGroups = (enabled: boolean) => {
  return useQuery({
    queryKey: ["basket-v2"],
    queryFn: getBasketV2,
    enabled,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

// Public stores marketplace listing
export const useStores = (search: string, ordering: StoresOrdering) => {
  return useInfiniteQuery({
    queryKey: ["stores", search, ordering],
    queryFn: ({ pageParam = 1 }) =>
      getStores({ search, ordering, page: pageParam, limit: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.total_pages
        ? lastPage.current_page + 1
        : undefined,
  });
};

// Bitta do'konning mahsulotlari (sahifalab, cheksiz skroll)
export const useStoreProducts = (address: string) => {
  return useInfiniteQuery({
    queryKey: ["storeProducts", address],
    queryFn: ({ pageParam = 1 }) => getStoreProducts(address, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.total_pages
        ? lastPage.current_page + 1
        : undefined,
    enabled: !!address,
  });
};
